import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/css';
import * as voicifyApi from '../../../../../shared/api';
import OrganizationVariablesContainer from '../../../../../shared/hooks/OrganizationVariablesContainer';
import OrganizationVariablesList from '../../../../../shared/scenes/organizationSettings/components/OrganizationVariablesList';
import SonioxContextEditor from '../../../../../shared/scenes/applicationSettings/components/telephonyDeployments/SonioxContextEditor';
import Button from '../../../../../shared/components/general/Button';
import PageError from '../../../../../shared/components/general/PageError';
import Loader from '../../../../../shared/components/general/Loader';
import useBulkAppOperations from '../../../../../shared/hooks/useBulkAppOperations';
import ApplicationModel from '../../../../../shared/models/applications/api/ApplicationModel';
import ApplicationContainer from '../../../../../shared/state/containers/ApplicationContainer';
import AppliedApplicationTemplateContainer from '../../../../../shared/state/containers/AppliedApplicationTemplateContainer';
import ApplicationEnvironmentsContainer from '../../../../../shared/state/containers/ApplicationEnvironmentsContainer';
import { dark_grey_blue, silver_two, color_shades_dark } from '../../../../../shared/constants/colors';
import { getBaseUrl, getSparkMainBaseUrl } from '../../../../../shared/constants/Urls';
import { TelephonyDeploymentResponse } from '../../../../../shared/models/telephonyDeployment/TelephonyDeploymentResponse';
import { getSonioxContextValidationError } from '../../../../../shared/scenes/applicationSettings/components/telephonyDeployments/sonioxContextUtils';
import { FoodOrderingFeatureFlagId, GenAIFoodOrdering } from '../../../../../shared/constants/featureFlagIds';
import { FoodOrderingGenAIModelSettingId, FoodOrderingGenAIPromptOverrideSettingId } from '../../../../../shared/constants/featureFlagSettingIds';
import { voiceOptions } from '../../../../../shared/constants/sparkConstants/voiceOptions';
import { GenAIModels } from '../../../../../shared/constants/genAiModels';

const getVoiceFriendlyName = (voiceString: string): string => {
    const match = voiceOptions.find(opt => opt.voice === voiceString);
    if (match) return match.value;

    if (voiceString?.includes('voice_id=')) {
        const idMatch = voiceString.match(/voice_id=([^|]+)/);
        if (idMatch) return `Custom (${idMatch[1].substring(0, 6)}...)`;
    }
    return 'Custom';
};

const getFeatureFlagSettingValue = (app: ApplicationModel, settingId: string): string | null => {
    if (!app.featureFlags) return null;
    for (const flag of app.featureFlags) {
        if (flag.featureFlagSettings) {
            const setting = flag.featureFlagSettings.find(s => s.id === settingId);
            if (setting) return setting.value || null;
        }
    }
    return null;
};

const formatDate = (dateStr: string | null | undefined): string => {
    if (!dateStr) return 'Never';
    try {
        const date = new Date(dateStr);
        return date.toLocaleDateString(undefined, {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    } catch {
        return dateStr;
    }
};

interface SparkOrganizationManagementSettingsProps {
    organizationId: string;
    locationApps?: ApplicationModel[];
    commonApp?: ApplicationModel;
    appliedTemplateContainer?: AppliedApplicationTemplateContainer;
    applicationEnvironmentsContainer?: ApplicationEnvironmentsContainer;
    appContainer?: ApplicationContainer;
}

interface ConnectionProfile {
    phoneNumber: string;
    asrProvider: string;
    proactiveResponsePrompt: string;
    ttsProvider: string;
    voiceString: string;
    friendlyName: string;
}

interface LocationStatus {
    lastSync: string | null;
    lastPublish: string | null;
    status: 'Idle' | 'Syncing...' | 'Publishing...' | 'Complete' | 'Failed' | null;
    hasModules: boolean;
    todaysHours: string | null;
    transferTarget: string | null;
    timeZones: string[];
    connections: ConnectionProfile[];
    rawDeployments: TelephonyDeploymentResponse[];
}

const SparkOrganizationManagementSettings: React.FC<SparkOrganizationManagementSettingsProps> = ({
    organizationId,
    locationApps: passedLocationApps = [],
    commonApp,
    appliedTemplateContainer,
    applicationEnvironmentsContainer,
    appContainer
}) => {
    const variablesContainer = OrganizationVariablesContainer.useContainer();
    const isMounted = React.useRef(true);
    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);

    const [asrContext, setAsrContext] = useState('');
    const [isLoaded, setIsLoaded] = useState(false);
    const [hasInitialized, setHasInitialized] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    
    // Collapsible states
    const [isAsrContextCollapsed, setIsAsrContextCollapsed] = useState(true);
    const [isOtherConstantsCollapsed, setIsOtherConstantsCollapsed] = useState(true);
    const [isPublishStatusCollapsed, setIsPublishStatusCollapsed] = useState(true);
    
    // Toast alerts
    const [errors, setErrors] = useState<string[]>([]);
    const [successes, setSuccesses] = useState<string[]>([]);

    // Edit Modal States
    const [editingDeployment, setEditingDeployment] = useState<TelephonyDeploymentResponse | null>(null);
    const [editingAppId, setEditingAppId] = useState<string>('');
    const [editType, setEditType] = useState<'asr' | 'proactive' | 'voice' | 'genaiModel' | 'genaiPrompt' | null>(null);
    const [editAsrProvider, setEditAsrProvider] = useState<string>('');
    const [editProactivePrompt, setEditProactivePrompt] = useState<string>('');
    const [editVoiceString, setEditVoiceString] = useState<string>('');
    const [editGenAIModel, setEditGenAIModel] = useState<string>('');
    const [editGenAIPrompt, setEditGenAIPrompt] = useState<string>('');
    const [isSavingTelemetry, setIsSavingTelemetry] = useState<boolean>(false);
    const [isLoadingOnDemand, setIsLoadingOnDemand] = useState<boolean>(false);

    const startEditAsr = (appId: string, deployment: TelephonyDeploymentResponse) => {
        setEditingAppId(appId);
        setEditingDeployment(deployment);
        setEditType('asr');
        setEditAsrProvider(deployment.asrProvider || 'Deepgram');
    };

    const startEditProactive = (appId: string, deployment: TelephonyDeploymentResponse) => {
        setEditingAppId(appId);
        setEditingDeployment(deployment);
        setEditType('proactive');
        setEditProactivePrompt(deployment.generativeProactiveResponsePrompt || '');
    };

    const startEditVoice = (appId: string, deployment: TelephonyDeploymentResponse) => {
        setEditingAppId(appId);
        setEditingDeployment(deployment);
        setEditType('voice');
        setEditVoiceString(deployment.ttsOptions?.[0]?.ttsVoice || deployment.ttsVoice || '');
    };

    const startEditGenAIModel = async (appId: string, currentApp: ApplicationModel) => {
        setEditingAppId(appId);
        setEditType('genaiModel');
        setIsLoadingOnDemand(true);
        setErrors([]);

        const targetAppId = appId;

        try {
            const detail = await voicifyApi.findApplication(targetAppId);
            if (detail?.resultType === 'Ok' && detail.data) {
                // Merge the loaded featureFlags onto the local location app state in memory
                setResolvedLocationApps(prev => prev.map(a => a.id === appId ? detail.data! : a));
                const val = getFeatureFlagSettingValue(detail.data, FoodOrderingGenAIModelSettingId) || 'gemini-2.5-flash';
                setEditGenAIModel(val);
            } else {
                setErrors(['Failed to load application GenAI settings.']);
            }
        } catch (err) {
            console.error('Failed to load application on-demand details:', err);
            setErrors(['Error loading GenAI settings.']);
        } finally {
            setIsLoadingOnDemand(false);
        }
    };

    const startEditGenAIPrompt = async (appId: string, currentApp: ApplicationModel) => {
        setEditingAppId(appId);
        setEditType('genaiPrompt');
        setIsLoadingOnDemand(true);
        setErrors([]);

        const targetAppId = appId;

        try {
            const detail = await voicifyApi.findApplication(targetAppId);
            if (detail?.resultType === 'Ok' && detail.data) {
                // Merge the loaded featureFlags onto the local location app state in memory
                setResolvedLocationApps(prev => prev.map(a => a.id === appId ? detail.data! : a));
                const val = getFeatureFlagSettingValue(detail.data, FoodOrderingGenAIPromptOverrideSettingId) || '';
                setEditGenAIPrompt(val);
            } else {
                setErrors(['Failed to load application GenAI settings.']);
            }
        } catch (err) {
            console.error('Failed to load application on-demand details:', err);
            setErrors(['Error loading GenAI settings.']);
        } finally {
            setIsLoadingOnDemand(false);
        }
    };

    const handleSaveTelemetrySettings = async () => {
        if (!editingAppId || !editType) return;
        setIsSavingTelemetry(true);
        setErrors([]);
        setSuccesses([]);

        try {
            if (editType === 'genaiModel' || editType === 'genaiPrompt') {
                const matchedApp = resolvedLocationApps.find(a => a.id === editingAppId);
                if (!matchedApp) throw new Error('Application not found.');

                const targetAppId = editingAppId;

                // 1. Fetch the master feature flags list to get the true, valid database schema
                const masterFlagsResult = await voicifyApi.getFeatureFlags();
                if (masterFlagsResult?.resultType !== 'Ok') throw new Error('Failed to retrieve master feature flag schemas.');

                const masterFlag = masterFlagsResult.data?.find(f => f.id === GenAIFoodOrdering);
                if (!masterFlag) throw new Error('GenAI Food Ordering feature flag schema not found on database.');

                const appFlag = matchedApp.featureFlags?.find(f => f.id === GenAIFoodOrdering);

                // 2. Clone the master settings array so we preserve all fields (name, type, renderOrder, description)
                const mergedSettings = (masterFlag.featureFlagSettings || []).map(masterSetting => {
                    const matchedAppSetting = appFlag?.featureFlagSettings?.find(s => s.id === masterSetting.id);
                    
                    // Default value is the application's current value or the master setting's default value
                    let value = matchedAppSetting?.value || masterSetting.value || '';

                    // Replace with the user's new custom value if this is the setting being edited
                    if (editType === 'genaiModel' && masterSetting.id === FoodOrderingGenAIModelSettingId) {
                        value = editGenAIModel;
                    } else if (editType === 'genaiPrompt' && masterSetting.id === FoodOrderingGenAIPromptOverrideSettingId) {
                        value = editGenAIPrompt;
                    }

                    return {
                        ...masterSetting,
                        value
                    };
                });

                const requestPayload = {
                    featureFlagId: GenAIFoodOrdering,
                    featureFlagSettings: mergedSettings
                };

                const response = await voicifyApi.updateApplicationFeatureFlagSettings(targetAppId, requestPayload);

                if (response?.resultType === 'Ok') {
                    setSuccesses([`Successfully updated GenAI feature flag settings for ${matchedApp.name}!`]);
                    
                    // Merge the newly updated featureFlags list onto the local Location App state in memory
                    setResolvedLocationApps(prev => prev.map(a => 
                        a.id === editingAppId ? { ...a, featureFlags: response.data!.featureFlags } : a
                    ));
                    closeTelemetryModal();
                } else if (response?.errors) {
                    setErrors(response.errors);
                } else {
                    setErrors(['Failed to update GenAI settings.']);
                }
            } else if (editingDeployment) {
                let request: any = { ...editingDeployment };

                if (editType === 'asr') {
                    request.asrProvider = editAsrProvider;
                } else if (editType === 'proactive') {
                    request.generativeProactiveResponsePrompt = editProactivePrompt;
                    request.useGenerativeProactiveResponse = true; // Ensure active
                } else if (editType === 'voice') {
                    const standardOption = voiceOptions.find(o => o.voice === editVoiceString);
                    const provider = standardOption ? standardOption.provider : 'ElevenLabs';

                    request.ttsProvider = provider;
                    request.ttsVoice = editVoiceString;
                    request.ttsOptions = [{ ttsProvider: provider, ttsVoice: editVoiceString }];
                }

                const response = await voicifyApi.updateTelephonyDeployment(editingAppId, editingDeployment.phoneNumber, request);

                if (response?.resultType === 'Ok') {
                    setSuccesses([`Successfully updated connection settings for ${editingDeployment.phoneNumber}!`]);
                    
                    // Optimistically update the single connection status locally in React memory instantly
                    setLocationStatuses(prev => {
                        const currentStatus = prev[editingAppId];
                        if (!currentStatus) return prev;

                        const updatedRawDeployments = (currentStatus.rawDeployments || []).map(d => 
                            d.phoneNumber === editingDeployment.phoneNumber ? response.data! : d
                        );

                        const updatedConnections = (currentStatus.connections || []).map(conn => {
                            if (conn.phoneNumber === editingDeployment.phoneNumber) {
                                const provider = response.data!.ttsOptions?.[0]?.ttsProvider || response.data!.ttsProvider || 'N/A';
                                const voiceString = response.data!.ttsOptions?.[0]?.ttsVoice || response.data!.ttsVoice || '';
                                return {
                                    phoneNumber: response.data!.phoneNumber || 'N/A',
                                    asrProvider: response.data!.asrProvider || 'N/A',
                                    proactiveResponsePrompt: response.data!.generativeProactiveResponsePrompt || 'None',
                                    ttsProvider: provider,
                                    voiceString,
                                    friendlyName: getVoiceFriendlyName(voiceString)
                                };
                            }
                            return conn;
                        });

                        return {
                            ...prev,
                            [editingAppId]: {
                                ...currentStatus,
                                rawDeployments: updatedRawDeployments,
                                connections: updatedConnections
                            }
                        };
                    });
                    
                    closeTelemetryModal();
                } else if (response?.errors) {
                    setErrors(response.errors);
                } else {
                    setErrors(['Failed to update connection settings.']);
                }
            }
        } catch (err: any) {
            console.error('Failed to update telephony settings:', err);
            setErrors([`Error saving connection settings: ${err.message || err.toString()}`]);
        } finally {
            setIsSavingTelemetry(false);
        }
    };

    const closeTelemetryModal = () => {
        setEditingDeployment(null);
        setEditingAppId('');
        setEditType(null);
        setEditAsrProvider('');
        setEditProactivePrompt('');
        setEditVoiceString('');
        setEditGenAIModel('');
        setEditGenAIPrompt('');
    };

    // Dynamically resolved apps (locations & shared topics) inside the component
    const [resolvedLocationApps, setResolvedLocationApps] = useState<ApplicationModel[]>([]);
    const [locationStatuses, setLocationStatuses] = useState<Record<string, LocationStatus>>({});
    const [isLoadingStatus, setIsLoadingStatus] = useState(false);

    // Bulk operations hooks
    const {
        isProcessing: isBulkProcessing,
        logs: bulkLogs,
        syncApps,
        publishApps,
        clearLogs
    } = useBulkAppOperations({
        appliedTemplateContainer,
        applicationEnvironmentsContainer
    });

    // Checkbox multi-select, search, and custom sorting states
    const [selectedAppIds, setSelectedAppIds] = useState<Set<string>>(new Set());
    const [filters, setFilters] = useState({
        name: '',
        isLive: '', // '', 'live', 'draft'
        lastSync: '',
        asr: '',
        proactive: '',
        voice: '',
        hours: '',
        target: '',
        model: '',
        prompt: ''
    });

    const [sort, setSort] = useState<{
        column: 'name' | 'isLive' | 'lastSync' | 'asr' | 'proactive' | 'voice' | 'hours' | 'target' | 'model' | 'prompt' | null;
        direction: 'asc' | 'desc' | null;
    }>({ column: null, direction: null });

    const handleFilterChange = (column: keyof typeof filters, value: string) => {
        setFilters(prev => ({
            ...prev,
            [column]: value
        }));
    };

    const handleSort = (column: typeof sort.column) => {
        setSort(prev => {
            if (prev.column === column) {
                if (prev.direction === 'asc') return { column, direction: 'desc' };
                return { column: null, direction: null }; // Clear sort
            }
            return { column, direction: 'asc' };
        });
    };

    const toggleSelectApp = (appId: string) => {
        setSelectedAppIds(prev => {
            const next = new Set(prev);
            if (next.has(appId)) {
                next.delete(appId);
            } else {
                next.add(appId);
            }
            return next;
        });
    };

    const toggleSelectAll = (appsList: ApplicationModel[]) => {
        const currentlySelectedCountForVisible = appsList.filter(app => selectedAppIds.has(app.id!)).length;
        const allSelected = currentlySelectedCountForVisible === appsList.length && appsList.length > 0;

        setSelectedAppIds(prev => {
            const next = new Set(prev);
            appsList.forEach(app => {
                if (allSelected) {
                    next.delete(app.id!);
                } else {
                    next.add(app.id!);
                }
            });
            return next;
        });
    };

    const selectAllLive = () => {
        const liveAppIds = resolvedLocationApps
            .filter(app => app.isLive && !app.allowsTemplating)
            .map(app => app.id!);
        setSelectedAppIds(new Set(liveAppIds));
    };

    const sortApps = (appsList: ApplicationModel[]): ApplicationModel[] => {
        if (!sort.column || !sort.direction) {
            return [...appsList].sort((a, b) => {
                const aName = (a.name || '').toLowerCase();
                const bName = (b.name || '').toLowerCase();
                const aIsTest = aName.includes('test') || aName.includes('sandbox');
                const bIsTest = bName.includes('test') || bName.includes('sandbox');
                const aIsShared = !!a.allowsTemplating;
                const bIsShared = !!b.allowsTemplating;

                if (aIsTest && bIsTest) return aName.localeCompare(bName);
                if (aIsTest) return 1;
                if (bIsTest) return -1;

                if (aIsShared && bIsShared) return aName.localeCompare(bName);
                if (aIsShared) return 1;
                if (bIsShared) return -1;

                return aName.localeCompare(bName);
            });
        }

        return [...appsList].sort((a, b) => {
            const statusA = locationStatuses[a.id!] || { lastSync: null, lastPublish: null, status: 'Idle', hasModules: true, asrProviders: [], proactiveResponsePrompts: [], todaysHours: null, transferTarget: null, voiceDetails: [] };
            const statusB = locationStatuses[b.id!] || { lastSync: null, lastPublish: null, status: 'Idle', hasModules: true, asrProviders: [], proactiveResponsePrompts: [], todaysHours: null, transferTarget: null, voiceDetails: [] };

            let valA: any = '';
            let valB: any = '';

            switch (sort.column) {
                case 'name':
                    valA = (a.name || '').toLowerCase();
                    valB = (b.name || '').toLowerCase();
                    break;
                case 'isLive':
                    valA = a.isLive ? 1 : 0;
                    valB = b.isLive ? 1 : 0;
                    break;
                case 'lastSync':
                    valA = statusA.lastSync ? new Date(statusA.lastSync).getTime() : 0;
                    valB = statusB.lastSync ? new Date(statusB.lastSync).getTime() : 0;
                    break;
                case 'asr':
                    valA = (statusA.connections?.map(c => c.asrProvider).join(', ') || '').toLowerCase();
                    valB = (statusB.connections?.map(c => c.asrProvider).join(', ') || '').toLowerCase();
                    break;
                case 'proactive':
                    valA = (statusA.proactiveResponsePrompts?.join(', ') || '').toLowerCase();
                    valB = (statusB.proactiveResponsePrompts?.join(', ') || '').toLowerCase();
                    break;
                case 'voice':
                    valA = (statusA.voiceDetails?.map(v => `${v.provider}: ${v.friendlyName}`).join(', ') || '').toLowerCase();
                    valB = (statusB.voiceDetails?.map(v => `${v.provider}: ${v.friendlyName}`).join(', ') || '').toLowerCase();
                    break;
                case 'hours':
                    valA = (statusA.todaysHours || '').toLowerCase();
                    valB = (statusB.todaysHours || '').toLowerCase();
                    break;
                case 'target':
                    valA = (statusA.transferTarget || '').toLowerCase();
                    valB = (statusB.transferTarget || '').toLowerCase();
                    break;
                case 'model':
                    valA = (getFeatureFlagSettingValue(a, FoodOrderingGenAIModelSettingId) || '').toLowerCase();
                    valB = (getFeatureFlagSettingValue(b, FoodOrderingGenAIModelSettingId) || '').toLowerCase();
                    break;
                case 'prompt':
                    valA = (getFeatureFlagSettingValue(a, FoodOrderingGenAIPromptOverrideSettingId) || '').toLowerCase();
                    valB = (getFeatureFlagSettingValue(b, FoodOrderingGenAIPromptOverrideSettingId) || '').toLowerCase();
                    break;
            }

            if (valA === valB) return 0;
            if (valA === null || valA === undefined || valA === '') return 1;
            if (valB === null || valB === undefined || valB === '') return -1;

            const res = typeof valA === 'string' ? valA.localeCompare(valB) : (valA > valB ? 1 : -1);
            return sort.direction === 'asc' ? res : -res;
        });
    };

    const sortedApps = sortApps(resolvedLocationApps);
    const visibleApps = sortedApps.filter(app => {
        const status = locationStatuses[app.id!] || { lastSync: null, lastPublish: null, status: 'Idle', hasModules: true, asrProviders: [], proactiveResponsePrompts: [], todaysHours: null, transferTarget: null, voiceDetails: [] };

        if (filters.name && !app.name?.toLowerCase().includes(filters.name.toLowerCase())) return false;

        if (filters.isLive === 'live' && !app.isLive) return false;
        if (filters.isLive === 'draft' && app.isLive) return false;

        const syncStr = status.hasModules ? formatDate(status.lastSync) : 'No module applied';
        if (filters.lastSync && !syncStr.toLowerCase().includes(filters.lastSync.toLowerCase())) return false;

        const asrStr = status.connections?.map(c => c.asrProvider).join(', ') || '';
        if (filters.asr && !asrStr.toLowerCase().includes(filters.asr.toLowerCase())) return false;

        const proactiveStr = status.connections?.map(c => c.proactiveResponsePrompt).join(', ') || '';
        if (filters.proactive && !proactiveStr.toLowerCase().includes(filters.proactive.toLowerCase())) return false;

        const voiceStr = status.voiceDetails?.map(v => `${v.provider}: ${v.friendlyName}`).join(', ') || '';
        if (filters.voice && !voiceStr.toLowerCase().includes(filters.voice.toLowerCase())) return false;

        const hoursStr = `hours: ${status.todaysHours || 'None'} phone: ${status.transferTarget || 'None'} timezone: ${status.timeZones?.join(', ') || 'None'}`;
        if (filters.hours && !hoursStr.toLowerCase().includes(filters.hours.toLowerCase())) return false;

        const modelStr = getFeatureFlagSettingValue(app, FoodOrderingGenAIModelSettingId) || 'Default';
        if (filters.model && !modelStr.toLowerCase().includes(filters.model.toLowerCase())) return false;

        const promptStr = getFeatureFlagSettingValue(app, FoodOrderingGenAIPromptOverrideSettingId) || 'None';
        if (filters.prompt && !promptStr.toLowerCase().includes(filters.prompt.toLowerCase())) return false;

        return true;
    });

    useEffect(() => {
        const load = async () => {
            if (organizationId) {
                try {
                    await variablesContainer.loadOrganizationVariablesByOrganizationId(organizationId);
                    setIsLoaded(true);
                } catch (err) {
                    console.error('Failed to load organization variables:', err);
                    setErrors(['Error loading organization constants.']);
                }
            }
        };
        load();
    }, [organizationId]);

    useEffect(() => {
        if (isLoaded && !hasInitialized && variablesContainer.organizationVariables) {
            const found = variablesContainer.organizationVariables.find(v => v.name === 'voicify_org_asr_context');
            if (found) {
                setAsrContext(found.value);
            }
            setHasInitialized(true);
        }
    }, [isLoaded, hasInitialized, variablesContainer.organizationVariables]);

    // Load and filter valid apps in the organization dynamically (all except manage menu apps)
    const loadApplications = async () => {
        if (!organizationId) return;
        try {
            const result = await voicifyApi.getApplicationsForOrg(organizationId);
            const apps = result?.data || [];

            // 1. Identify which apps are manage menu apps (referenced or name heuristics)
            const manageMenuAppIds = new Set(
                apps.map(a => a.manageMenuApplicationId).filter(Boolean)
            );
            const isManageMenuApp = (a: ApplicationModel) =>
                manageMenuAppIds.has(a.id) ||
                (a?.name?.toLowerCase()?.startsWith("x -") && a?.name?.toLowerCase()?.endsWith("menu")) ||
                a?.name?.toLowerCase()?.includes("manage menu") ||
                a?.name?.toLowerCase()?.includes("manage entities");

            // 2. Filter apps (include location apps & allowsTemplating shared topics apps; exclude ONLY manage menu apps)
            const filtered = apps.filter(a => !isManageMenuApp(a));

            setResolvedLocationApps(filtered);
        } catch (err) {
            console.error('Failed to retrieve applications:', err);
            setErrors(prev => [...prev, 'Failed to retrieve applications in this organization.']);
        }
    };

    // Fetch and sync the module sync and publish timestamps for all resolved locations
    const loadLocationStatuses = async (appsToLoad = resolvedLocationApps) => {
        if (!appsToLoad || appsToLoad.length === 0) return;
        const statuses: Record<string, LocationStatus> = { ...locationStatuses };

        // Pre-initialize empty statuses for any new apps to load
        appsToLoad.forEach(app => {
            if (!statuses[app.id!]) {
                statuses[app.id!] = { lastSync: null, lastPublish: null, status: 'Idle', hasModules: true, todaysHours: null, transferTarget: null, timeZones: [], connections: [], rawDeployments: [] };
            }
        });
        setLocationStatuses({ ...statuses });

        const concurrencyLimit = 5;
        let appQueue = [...appsToLoad];

        const worker = async () => {
            while (appQueue.length > 0) {
                const app = appQueue.shift();
                if (!app) break;

                const appId = app.id!;
                let lastSync: string | null = null;
                let lastPublish: string | null = null;
                let hasModules = true;
                let todaysHours: string | null = null;
                let transferTarget: string | null = null;
                let timeZones: string[] = [];
                let connections: ConnectionProfile[] = [];
                let rawDeployments: TelephonyDeploymentResponse[] = [];

                try {
                    // Check if there are any applied templates/modules
                    if (appliedTemplateContainer) {
                        const templatesResult = await appliedTemplateContainer.loadAppliedTemplates(appId);
                        hasModules = !!(templatesResult?.resultType === 'Ok' && templatesResult.data?.length > 0);

                        if (hasModules && templatesResult.data) {
                            const days = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];
                            const todayDayName = days[new Date().getDay()];
                            const todayVariable = `{${todayDayName}}`;

                            for (const template of templatesResult.data) {
                                if (template.templateFormFieldValues) {
                                    for (const fieldValue of template.templateFormFieldValues) {
                                        const assocVar = (fieldValue.associatedVariable || '').trim();
                                        const assocVarUpper = assocVar.toUpperCase();

                                        // 1. Match today's hours
                                        if (assocVarUpper === todayVariable || assocVarUpper === todayDayName) {
                                            todaysHours = fieldValue.value || null;
                                        }

                                        // 2. Match transfer target
                                        if (
                                            assocVar === '{TransferTarget}' || 
                                            assocVar === 'TransferTarget' || 
                                            assocVar === '{{session.returntostorenumber}}' ||
                                            assocVar === 'session.returntostorenumber'
                                        ) {
                                            transferTarget = fieldValue.value || null;
                                        }

                                        // 3. Match Time Zone ({TIME_ZONE} or {Webhook-Time-Zone})
                                        if (
                                            assocVarUpper === '{TIME_ZONE}' || 
                                            assocVarUpper === 'TIME_ZONE' ||
                                            assocVarUpper === '{WEBHOOK-TIME-ZONE}' ||
                                            assocVarUpper === 'WEBHOOK-TIME-ZONE'
                                        ) {
                                            if (fieldValue.value) {
                                                timeZones.push(fieldValue.value);
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }

                    // 1. Fetch completed module sync history (only if modules exist)
                    if (hasModules) {
                        const syncsResult = await voicifyApi.getAppliedTemplateSyncsForApplication(appId);
                        if (syncsResult?.resultType === 'Ok' && syncsResult.data?.length > 0) {
                            const completedSyncs = syncsResult.data
                                .filter(s => s.stage === 'Complete' && s.createdDate)
                                .sort((a, b) => new Date(b.createdDate!).getTime() - new Date(a.createdDate!).getTime());
                            if (completedSyncs.length > 0) {
                                lastSync = completedSyncs[0].createdDate!;
                            }
                        }
                    }

                    // 2. Fetch completed publish history (Skip publishing metrics for templated/shared topics apps)
                    if (!app.allowsTemplating) {
                        const assistantAppResult = await voicifyApi.getAssistantApplication(appId);
                        if (assistantAppResult?.resultType === 'Ok' && assistantAppResult.data?.environments) {
                            const productionEnv = assistantAppResult.data.environments.find(e => e.name === 'Production');
                            if (productionEnv) {
                                const publishesResult = await voicifyApi.getEnvironmentPublishes(productionEnv.id, 0, 1);
                                if (publishesResult?.resultType === 'Ok' && publishesResult.data?.length > 0) {
                                    lastPublish = publishesResult.data[0].createdDate || null;
                                }
                            }
                        }
                    }

                    // 3. Fetch telephony connection details (ASR, Proactive Response Prompts, and Voices)
                    if (!app.allowsTemplating) {
                        const telephonyResult = await voicifyApi.getTelephonyDeployments(appId);
                        if (telephonyResult?.resultType === 'Ok' && telephonyResult.data?.length > 0) {
                            rawDeployments = telephonyResult.data;
                            connections = telephonyResult.data.map(d => {
                                const provider = d.ttsOptions?.[0]?.ttsProvider || d.ttsProvider || 'N/A';
                                const voiceString = d.ttsOptions?.[0]?.ttsVoice || d.ttsVoice || '';
                                return {
                                    phoneNumber: d.phoneNumber || 'N/A',
                                    asrProvider: d.asrProvider || 'N/A',
                                    proactiveResponsePrompt: d.generativeProactiveResponsePrompt || 'None',
                                    ttsProvider: provider,
                                    voiceString,
                                    friendlyName: getVoiceFriendlyName(voiceString)
                                };
                            });
                        }
                    }
                } catch (innerErr) {
                    console.error(`Failed to fetch sync/publish dates for ${app.name}:`, innerErr);
                }

                // Update state incrementally so we cascade load rows smoothly!
                if (isMounted.current) {
                    setLocationStatuses(prev => ({
                        ...prev,
                        [appId]: {
                            lastSync,
                            lastPublish,
                            status: prev[appId]?.status || 'Idle',
                            hasModules,
                            todaysHours,
                            transferTarget,
                            timeZones,
                            connections,
                            rawDeployments
                        }
                    }));
                }
            }
        };

        try {
            const workers = [];
            for (let i = 0; i < Math.min(concurrencyLimit, appsToLoad.length); i++) {
                workers.push(worker());
            }
            await Promise.all(workers);
        } catch (err) {
            console.error('Failed to load location statuses via concurrency queue:', err);
        }
    };

    // Load location list and statuses when panel is expanded
    useEffect(() => {
        if (!isPublishStatusCollapsed && organizationId) {
            const fetchAll = async () => {
                setIsLoadingStatus(true);
                await loadApplications();
                setIsLoadingStatus(false);
            };
            fetchAll();
        }
    }, [isPublishStatusCollapsed, organizationId]);

    useEffect(() => {
        if (resolvedLocationApps.length > 0) {
            const hasLoaded = Object.keys(locationStatuses).length > 0;
            if (!hasLoaded) {
                loadLocationStatuses(resolvedLocationApps);
            }
        }
    }, [resolvedLocationApps.length, organizationId]);

    const handleSave = async () => {
        setErrors([]);
        setSuccesses([]);

        let valueToSave = asrContext;
        if (!valueToSave || valueToSave.trim() === '') {
            valueToSave = '{}';
        }

        const validationError = getSonioxContextValidationError(valueToSave, 20000);
        if (validationError) {
            setErrors([validationError]);
            return;
        }

        setIsSaving(true);
        try {
            const existing = variablesContainer.organizationVariables?.find(v => v.name === 'voicify_org_asr_context');
            let result;

            if (existing) {
                result = await variablesContainer.updateOrganizationVariable(existing.id!, {
                    ...existing,
                    value: valueToSave
                });
            } else {
                result = await variablesContainer.createOrganizationVariable({
                    name: 'voicify_org_asr_context',
                    value: valueToSave,
                    organizationId: organizationId
                });
            }

            if (result?.resultType === 'Ok') {
                setSuccesses(['ASR Custom Context saved successfully! Note: The application must be republished to pick up updates to this constant.']);
            } else if (result?.errors) {
                setErrors(result.errors);
            } else {
                setErrors(['Failed to save ASR Custom Context.']);
            }
        } catch (err) {
            console.error('Error saving organization variable:', err);
            setErrors(['An unexpected error occurred while saving.']);
        } finally {
            setIsSaving(false);
        }
    };

    // Sync or Sync & Publish a single location sequentially
    const handleSyncAndPublishLocation = async (app: ApplicationModel) => {
        setErrors([]);
        setSuccesses([]);

        const appStatus = locationStatuses[app.id!];
        const hasModules = appStatus?.hasModules ?? true;

        try {
            if (app.allowsTemplating) {
                // Shared topics: Sync only
                setLocationStatuses(prev => ({
                    ...prev,
                    [app.id!]: { ...prev[app.id!], status: 'Syncing...' }
                }));
                const syncResult = await syncApps([app], null, 1);
                if (syncResult.errorCount > 0) {
                    throw new Error('Module sync failed.');
                }
                setLocationStatuses(prev => ({
                    ...prev,
                    [app.id!]: { ...prev[app.id!], status: 'Complete' }
                }));
                setSuccesses([`Successfully synced ${app.name}!`]);
            } else if (!hasModules) {
                // No modules: Publish only
                setLocationStatuses(prev => ({
                    ...prev,
                    [app.id!]: { ...prev[app.id!], status: 'Publishing...' }
                }));
                const publishResult = await publishApps([app], 1);
                if (publishResult.errorCount > 0) {
                    throw new Error('Full publish failed.');
                }
                setLocationStatuses(prev => ({
                    ...prev,
                    [app.id!]: { ...prev[app.id!], status: 'Complete' }
                }));
                setSuccesses([`Successfully published ${app.name}!`]);
            } else {
                // Regular location: Sync & Publish
                setLocationStatuses(prev => ({
                    ...prev,
                    [app.id!]: { ...prev[app.id!], status: 'Syncing...' }
                }));
                const syncResult = await syncApps([app], commonApp, 1);
                if (syncResult.errorCount > 0) {
                    throw new Error('Module sync failed.');
                }

                // Update status to Publishing
                setLocationStatuses(prev => ({
                    ...prev,
                    [app.id!]: { ...prev[app.id!], status: 'Publishing...' }
                }));

                // Publish templates
                const publishResult = await publishApps([app], 1);
                if (publishResult.errorCount > 0) {
                    throw new Error('Template publish failed.');
                }

                setLocationStatuses(prev => ({
                    ...prev,
                    [app.id!]: { ...prev[app.id!], status: 'Complete' }
                }));
                setSuccesses([`Successfully synced and published ${app.name}!`]);
            }
            await loadLocationStatuses();
        } catch (err: any) {
            console.error(`Error during sync/publish for ${app.name}:`, err);
            setLocationStatuses(prev => ({
                ...prev,
                [app.id!]: { ...prev[app.id!], status: 'Failed' }
            }));
            setErrors([`Failed to complete operation for ${app.name}: ${err.message || err.toString()}`]);
        }
    };

    // Sync & Publish all locations sequentially
    const handleSyncAndPublishAll = async () => {
        if (!resolvedLocationApps || resolvedLocationApps.length === 0) return;
        setErrors([]);
        setSuccesses([]);

        const startingStatuses = { ...locationStatuses };
        resolvedLocationApps.forEach(app => {
            startingStatuses[app.id!] = { ...startingStatuses[app.id!], status: 'Syncing...' };
        });
        setLocationStatuses(startingStatuses);

        try {
            // Segregate templates, standard publishable locations, and publish-only empty locations
            const sharedTopicsApp = resolvedLocationApps.find(app => app.allowsTemplating);
            const syncAndPublishApps = resolvedLocationApps.filter(app => !app.allowsTemplating && locationStatuses[app.id!]?.hasModules);
            const publishOnlyApps = resolvedLocationApps.filter(app => !app.allowsTemplating && !locationStatuses[app.id!]?.hasModules);

            // 1. Sync standard locations (with modules) with a concurrency limit of 5 parallel workers
            if (syncAndPublishApps.length > 0) {
                const syncResult = await syncApps(syncAndPublishApps, sharedTopicsApp, 5);
                if (syncResult.errorCount > 0) {
                    throw new Error(`Bulk sync completed with ${syncResult.errorCount} failures.`);
                }
            }

            const publishingStatuses = { ...locationStatuses };
            resolvedLocationApps.forEach(app => {
                if (app.allowsTemplating) {
                    publishingStatuses[app.id!] = { ...publishingStatuses[app.id!], status: 'Complete' };
                } else {
                    publishingStatuses[app.id!] = { ...publishingStatuses[app.id!], status: 'Publishing...' };
                }
            });
            setLocationStatuses(publishingStatuses);

            // 2. Publish standard locations AND empty-module locations together in parallel (limit 5)
            const allPublishApps = [...syncAndPublishApps, ...publishOnlyApps];
            if (allPublishApps.length > 0) {
                const publishResult = await publishApps(allPublishApps, 5);
                if (publishResult.errorCount > 0) {
                    throw new Error(`Bulk publish completed with ${publishResult.errorCount} failures.`);
                }
            }

            const finalStatuses = { ...locationStatuses };
            resolvedLocationApps.forEach(app => {
                finalStatuses[app.id!] = { ...finalStatuses[app.id!], status: 'Complete' };
            });
            setLocationStatuses(finalStatuses);
            setSuccesses(['Successfully synced and published all locations!']);
            await loadLocationStatuses();
        } catch (err: any) {
            console.error('Error during bulk sync & publish:', err);
            const errorStatuses = { ...locationStatuses };
            resolvedLocationApps.forEach(app => {
                errorStatuses[app.id!] = { ...errorStatuses[app.id!], status: 'Failed' };
            });
            setLocationStatuses(errorStatuses);
            setErrors([`Bulk operation completed with errors: ${err.message || err.toString()}`]);
        }
    };

    // Sync & Publish only selected locations sequentially
    const handleSyncAndPublishSelected = async () => {
        const appsToPublish = resolvedLocationApps.filter(app => selectedAppIds.has(app.id!));
        if (!appsToPublish || appsToPublish.length === 0) return;
        setErrors([]);
        setSuccesses([]);

        const startingStatuses = { ...locationStatuses };
        appsToPublish.forEach(app => {
            startingStatuses[app.id!] = { ...startingStatuses[app.id!], status: 'Syncing...' };
        });
        setLocationStatuses(startingStatuses);

        try {
            // Segregate templates, standard publishable locations, and publish-only empty locations
            const sharedTopicsApp = appsToPublish.find(app => app.allowsTemplating);
            const syncAndPublishApps = appsToPublish.filter(app => !app.allowsTemplating && locationStatuses[app.id!]?.hasModules);
            const publishOnlyApps = appsToPublish.filter(app => !app.allowsTemplating && !locationStatuses[app.id!]?.hasModules);

            // 1. Sync selected standard locations (with modules) with a concurrency limit of 5 parallel workers
            if (syncAndPublishApps.length > 0) {
                const syncResult = await syncApps(syncAndPublishApps, sharedTopicsApp, 5);
                if (syncResult.errorCount > 0) {
                    throw new Error(`Bulk sync completed with ${syncResult.errorCount} failures.`);
                }
            }

            const publishingStatuses = { ...locationStatuses };
            appsToPublish.forEach(app => {
                if (app.allowsTemplating) {
                    publishingStatuses[app.id!] = { ...publishingStatuses[app.id!], status: 'Complete' };
                } else {
                    publishingStatuses[app.id!] = { ...publishingStatuses[app.id!], status: 'Publishing...' };
                }
            });
            setLocationStatuses(publishingStatuses);

            // 2. Publish selected standard locations AND empty-module locations together in parallel (limit 5)
            const allPublishApps = [...syncAndPublishApps, ...publishOnlyApps];
            if (allPublishApps.length > 0) {
                const publishResult = await publishApps(allPublishApps, 5);
                if (publishResult.errorCount > 0) {
                    throw new Error(`Bulk publish completed with ${publishResult.errorCount} failures.`);
                }
            }

            const finalStatuses = { ...locationStatuses };
            appsToPublish.forEach(app => {
                finalStatuses[app.id!] = { ...finalStatuses[app.id!], status: 'Complete' };
            });
            setLocationStatuses(finalStatuses);
            setSuccesses([`Successfully synced and published ${appsToPublish.length} selected locations!`]);
            setSelectedAppIds(new Set()); // Clear selection upon successful operation
            await loadLocationStatuses();
        } catch (err: any) {
            console.error('Error during selected bulk sync & publish:', err);
            const errorStatuses = { ...locationStatuses };
            appsToPublish.forEach(app => {
                errorStatuses[app.id!] = { ...errorStatuses[app.id!], status: 'Failed' };
            });
            setLocationStatuses(errorStatuses);
            setErrors([`Selected bulk operation completed with errors: ${err.message || err.toString()}`]);
        }
    };

    const getSortIcon = (columnName: typeof sort.column) => {
        if (sort.column !== columnName) return '↕';
        return sort.direction === 'asc' ? '▲' : '▼';
    };

    return (
        <Container>
            {/* 1. Collapsible Speech Recognition Context Card */}
            <CollapsibleCard>
                <CollapsibleHeader onClick={() => setIsAsrContextCollapsed(!isAsrContextCollapsed)}>
                    <h3>Speech Recognition Context</h3>
                    <ToggleIcon>{isAsrContextCollapsed ? '▼' : '▲'}</ToggleIcon>
                </CollapsibleHeader>
                
                {!isAsrContextCollapsed && (
                    <CollapsibleBody style={{ padding: '24px 32px' }}>
                        <SonioxContextEditor
                            name="voicify_org_asr_context"
                            title="Organization ASR Hints"
                            description="Configure custom terms, vocabulary, or other hints that apply to all voice experiences in this organization."
                            value={asrContext}
                            onChange={(val) => setAsrContext(val)}
                            onBlur={() => {}}
                            onTouch={() => {}}
                            maxLength={20000}
                            showFieldError={false}
                        />
                        
                        <SaveButtonWrapper>
                            <Button
                                themes={['primary']}
                                text={isSaving ? 'Saving...' : 'Save Context'}
                                onClick={handleSave}
                                disabled={isSaving}
                            />
                        </SaveButtonWrapper>
                    </CollapsibleBody>
                )}
            </CollapsibleCard>

            {/* 2. Collapsible Other Organization Constants Card */}
            <CollapsibleCard>
                <CollapsibleHeader onClick={() => setIsOtherConstantsCollapsed(!isOtherConstantsCollapsed)}>
                    <h3>Other Organization Constants</h3>
                    <ToggleIcon>{isOtherConstantsCollapsed ? '▼' : '▲'}</ToggleIcon>
                </CollapsibleHeader>
                
                {!isOtherConstantsCollapsed && (
                    <CollapsibleBody>
                        <OrganizationVariablesList organizationId={organizationId} />
                    </CollapsibleBody>
                )}
            </CollapsibleCard>

            {/* 3. Collapsible Location Status Card */}
            <CollapsibleCard>
                <CollapsibleHeader onClick={() => setIsPublishStatusCollapsed(!isPublishStatusCollapsed)}>
                    <h3>Location Status</h3>
                    <ToggleIcon>{isPublishStatusCollapsed ? '▼' : '▲'}</ToggleIcon>
                </CollapsibleHeader>

                {!isPublishStatusCollapsed && (
                    <CollapsibleBody style={{ padding: '24px 32px' }}>
                        <BulkActionsBar style={{ justifyContent: 'space-between' }}>
                            {resolvedLocationApps.length > 0 && (
                                <SelectAllLiveButton onClick={selectAllLive}>
                                    Select All Live Locations
                                </SelectAllLiveButton>
                            )}
                            <BulkButtonsGroup>
                                <Button
                                    themes={['secondary']}
                                    text={`Sync & Publish Selected (${selectedAppIds.size})`}
                                    onClick={handleSyncAndPublishSelected}
                                    disabled={isBulkProcessing || selectedAppIds.size === 0}
                                />
                                <Button
                                    themes={['primary']}
                                    text="Sync & Publish All Locations"
                                    onClick={handleSyncAndPublishAll}
                                    disabled={isBulkProcessing || resolvedLocationApps.length === 0}
                                />
                            </BulkButtonsGroup>
                        </BulkActionsBar>

                        {/* Minimalist plain text progress logs */}
                        {bulkLogs.length > 0 && (
                            <LogSection>
                                <LogHeader>
                                    <span>Sync & Publish Progress Logs</span>
                                    {!isBulkProcessing && (
                                        <Button
                                            themes={['secondary-small']}
                                            text="Clear Logs"
                                            onClick={clearLogs}
                                        />
                                    )}
                                </LogHeader>
                                <LogArea>
                                    {bulkLogs.map((log, index) => (
                                        <LogLine key={index}>{log}</LogLine>
                                    ))}
                                </LogArea>
                            </LogSection>
                        )}

                        {isLoadingStatus ? (
                            <LoaderContainer>
                                <Loader />
                                <LoadingText>Fetching last sync and publish metrics...</LoadingText>
                            </LoaderContainer>
                        ) : visibleApps.length === 0 ? (
                            <EmptyState>No active locations found matching your search.</EmptyState>
                        ) : (
                            <TableContainer>
                                <Table>
                                    <thead>
                                        <tr>
                                            <Th style={{ width: '40px', verticalAlign: 'middle' }}>
                                                <input 
                                                    type="checkbox" 
                                                    checked={visibleApps.length > 0 && visibleApps.every(app => selectedAppIds.has(app.id!))}
                                                    onChange={() => toggleSelectAll(visibleApps)}
                                                />
                                            </Th>
                                            <Th style={{ paddingLeft: '8px', verticalAlign: 'middle' }}>
                                                <ThContent onClick={() => handleSort('name')}>
                                                    <span>Location</span>
                                                    <SortIndicator>{getSortIcon('name')}</SortIndicator>
                                                </ThContent>
                                            </Th>
                                            <Th style={{ verticalAlign: 'middle' }}>
                                                <ThContent onClick={() => handleSort('isLive')}>
                                                    <span>Live Status</span>
                                                    <SortIndicator>{getSortIcon('isLive')}</SortIndicator>
                                                </ThContent>
                                            </Th>
                                            <Th style={{ verticalAlign: 'middle' }}>
                                                <ThContent onClick={() => handleSort('lastSync')}>
                                                    <span>Synchronization</span>
                                                    <SortIndicator>{getSortIcon('lastSync')}</SortIndicator>
                                                </ThContent>
                                            </Th>
                                            <Th style={{ verticalAlign: 'middle' }}>
                                                <ThContent onClick={() => handleSort('asr')}>
                                                    <span>ASR & Proactive Prompt</span>
                                                    <SortIndicator>{getSortIcon('asr')}</SortIndicator>
                                                </ThContent>
                                            </Th>
                                            <Th style={{ verticalAlign: 'middle' }}>
                                                <ThContent onClick={() => handleSort('voice')}>
                                                    <span>Voice</span>
                                                    <SortIndicator>{getSortIcon('voice')}</SortIndicator>
                                                </ThContent>
                                            </Th>
                                            <Th style={{ verticalAlign: 'middle' }}>
                                                <ThContent onClick={() => handleSort('hours')}>
                                                    <span>Module Fields</span>
                                                    <SortIndicator>{getSortIcon('hours')}</SortIndicator>
                                                </ThContent>
                                            </Th>
                                            <Th style={{ verticalAlign: 'middle' }}>
                                                <ThContent onClick={() => handleSort('model')}>
                                                    <span>Ordering GenAI</span>
                                                    <SortIndicator>{getSortIcon('model')}</SortIndicator>
                                                </ThContent>
                                            </Th>
                                            <Th style={{ textAlign: 'right', verticalAlign: 'middle' }}>Actions</Th>
                                        </tr>
                                        <tr>
                                            <Th style={{ width: '40px', paddingBottom: '8px' }}></Th>
                                            <Th style={{ paddingLeft: '8px', paddingBottom: '8px' }}>
                                                <ColumnFilterInput 
                                                    placeholder="Filter Location..." 
                                                    value={filters.name}
                                                    onChange={(e) => handleFilterChange('name', e.target.value)}
                                                />
                                            </Th>
                                            <Th style={{ paddingBottom: '8px' }}>
                                                <ColumnFilterSelect 
                                                    value={filters.isLive}
                                                    onChange={(e) => handleFilterChange('isLive', e.target.value)}
                                                >
                                                    <option value="">All Statuses</option>
                                                    <option value="live">Live Only</option>
                                                    <option value="draft">Draft Only</option>
                                                </ColumnFilterSelect>
                                            </Th>
                                            <Th style={{ paddingBottom: '8px' }}>
                                                <ColumnFilterInput 
                                                    placeholder="Filter Sync..." 
                                                    value={filters.lastSync}
                                                    onChange={(e) => handleFilterChange('lastSync', e.target.value)}
                                                />
                                            </Th>
                                            <Th style={{ paddingBottom: '8px' }}>
                                                <ColumnFilterInput 
                                                    placeholder="Filter ASR/Prompt..." 
                                                    value={filters.asr}
                                                    onChange={(e) => handleFilterChange('asr', e.target.value)}
                                                />
                                            </Th>
                                            <Th style={{ paddingBottom: '8px' }}>
                                                <ColumnFilterInput 
                                                    placeholder="Filter Voice..." 
                                                    value={filters.voice}
                                                    onChange={(e) => handleFilterChange('voice', e.target.value)}
                                                />
                                            </Th>
                                            <Th style={{ paddingBottom: '8px' }}>
                                                <ColumnFilterInput 
                                                    placeholder="Filter Module Fields..." 
                                                    value={filters.hours}
                                                    onChange={(e) => handleFilterChange('hours', e.target.value)}
                                                />
                                            </Th>
                                            <Th style={{ paddingBottom: '8px' }}>
                                                <ColumnFilterInput 
                                                    placeholder="Filter Model/Prompt..." 
                                                    value={filters.model}
                                                    onChange={(e) => handleFilterChange('model', e.target.value)}
                                                />
                                            </Th>
                                            <Th style={{ paddingBottom: '8px' }}></Th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {visibleApps.map(app => {
                                            const status = locationStatuses[app.id!] || { lastSync: null, lastPublish: null, status: 'Idle', hasModules: true, todaysHours: null, transferTarget: null, timeZones: [], connections: [], rawDeployments: [] };
                                            return (
                                                <tr key={app.id}>
                                                    <Td style={{ width: '40px' }}>
                                                        <input 
                                                            type="checkbox" 
                                                            checked={selectedAppIds.has(app.id!)}
                                                            onChange={() => toggleSelectApp(app.id!)}
                                                        />
                                                    </Td>
                                                    <Td style={{ paddingLeft: '8px' }}>
                                                        <CellRow style={{ fontWeight: 700 }}>
                                                            <LocationLink 
                                                                href={`${getBaseUrl()}/v/apps/${app.id}`} 
                                                                target="_blank" 
                                                                rel="noopener noreferrer"
                                                            >
                                                                {app.name}
                                                            </LocationLink>
                                                        </CellRow>
                                                    </Td>
                                                    <Td style={{ verticalAlign: 'middle' }}>
                                                        {!app.allowsTemplating && (
                                                            <InlineLivePill isLive={!!app.isLive}>
                                                                {app.isLive ? 'Live' : 'Draft'}
                                                            </InlineLivePill>
                                                        )}
                                                    </Td>
                                                    <Td>
                                                        <CellRow>
                                                            <span style={{ fontWeight: 'bold' }}>SYNC: </span>
                                                            {status.hasModules ? (
                                                                formatDate(status.lastSync)
                                                            ) : (
                                                                <NoModuleBadge>No module applied</NoModuleBadge>
                                                            )}
                                                        </CellRow>
                                                        <CellRow>
                                                            <span style={{ fontWeight: 'bold' }}>PUB: </span>
                                                            {app.allowsTemplating ? 'N/A' : formatDate(status.lastPublish)}
                                                        </CellRow>
                                                    </Td>
                                                    <Td>
                                                        {app.allowsTemplating ? (
                                                            <CellRow>N/A</CellRow>
                                                        ) : (
                                                            status.connections && status.connections.length > 0 ? (
                                                                status.connections.map((conn, i) => (
                                                                    <ConnectionBlock 
                                                                        key={i} 
                                                                        style={{ 
                                                                            borderBottom: i < status.connections.length - 1 ? '1px dashed #e2e8f0' : 'none', 
                                                                            paddingBottom: i < status.connections.length - 1 ? '8px' : '0', 
                                                                            paddingTop: i > 0 ? '8px' : '0' 
                                                                        }}
                                                                    >
                                                                        <CellRow>
                                                                            <span style={{ fontWeight: 'bold' }}>ASR: </span>
                                                                            <EditableValue 
                                                                                title={`Click to edit ASR Provider for ${conn.phoneNumber}`}
                                                                                onClick={() => {
                                                                                    const rawDep = status.rawDeployments?.[i];
                                                                                    if (rawDep) startEditAsr(app.id!, rawDep);
                                                                                }}
                                                                            >
                                                                                {conn.asrProvider}
                                                                            </EditableValue> ({conn.phoneNumber})
                                                                        </CellRow>
                                                                        <CellRow style={{ display: 'flex', alignItems: 'center' }}>
                                                                            <span style={{ fontWeight: 'bold', marginRight: '4px' }}>PROACTIVE: </span>
                                                                            <EditableValue 
                                                                                style={{ 
                                                                                    maxWidth: '180px', 
                                                                                    overflow: 'hidden', 
                                                                                    textOverflow: 'ellipsis', 
                                                                                    whiteSpace: 'nowrap'
                                                                                }} 
                                                                                title={`Click to edit Proactive Prompt (Hover to see full text):\n${conn.proactiveResponsePrompt}`}
                                                                                onClick={() => {
                                                                                    const rawDep = status.rawDeployments?.[i];
                                                                                    if (rawDep) startEditProactive(app.id!, rawDep);
                                                                                }}
                                                                            >
                                                                                {conn.proactiveResponsePrompt}
                                                                            </EditableValue>
                                                                        </CellRow>
                                                                    </ConnectionBlock>
                                                                ))
                                                            ) : (
                                                                <ConnectionBlock>
                                                                    <CellRow>N/A</CellRow>
                                                                </ConnectionBlock>
                                                            )
                                                        )}
                                                    </Td>
                                                    <Td>
                                                        {app.allowsTemplating ? (
                                                            <CellRow>N/A</CellRow>
                                                        ) : (
                                                            status.connections && status.connections.length > 0 ? (
                                                                status.connections.map((conn, i) => (
                                                                    <ConnectionBlock 
                                                                        key={i} 
                                                                        style={{ 
                                                                            borderBottom: i < status.connections.length - 1 ? '1px dashed #e2e8f0' : 'none', 
                                                                            paddingBottom: i < status.connections.length - 1 ? '8px' : '0', 
                                                                            paddingTop: i > 0 ? '8px' : '0' 
                                                                        }}
                                                                    >
                                                                        <CellRow style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                                                                            <span style={{ fontWeight: 'bold' }}>VOICE: </span>
                                                                            <EditableValue 
                                                                                title={`Click to edit Voice (Hover to see full voice string):\n${conn.voiceString}`}
                                                                                onClick={() => {
                                                                                    const rawDep = status.rawDeployments?.[i];
                                                                                    if (rawDep) startEditVoice(app.id!, rawDep);
                                                                                }}
                                                                            >
                                                                                <strong style={{ textTransform: 'capitalize' }}>{conn.ttsProvider}</strong>: {conn.friendlyName}
                                                                            </EditableValue>
                                                                        </CellRow>
                                                                    </ConnectionBlock>
                                                                ))
                                                            ) : (
                                                                <ConnectionBlock>
                                                                    <CellRow>N/A</CellRow>
                                                                </ConnectionBlock>
                                                            )
                                                        )}
                                                    </Td>
                                                    <Td>
                                                        <CellRow>
                                                            <CellLink 
                                                                href={`${getSparkMainBaseUrl()}/v/apps/${app.id}`} 
                                                                target="_blank" 
                                                                rel="noopener noreferrer"
                                                                title={`Click to open Spark Form for ${app.name}`}
                                                            >
                                                                <span style={{ fontWeight: 'bold' }}>Hours Today: </span>
                                                                {status.todaysHours || 'None'}
                                                            </CellLink>
                                                        </CellRow>
                                                        <CellRow>
                                                            <CellLink 
                                                                href={`${getSparkMainBaseUrl()}/v/apps/${app.id}`} 
                                                                target="_blank" 
                                                                rel="noopener noreferrer"
                                                                title={`Click to open Spark Form for ${app.name}`}
                                                            >
                                                                <span style={{ fontWeight: 'bold' }}>Transfer Number: </span>
                                                                {status.transferTarget || 'None'}
                                                            </CellLink>
                                                        </CellRow>
                                                        <CellRow>
                                                            <CellLink 
                                                                href={`${getSparkMainBaseUrl()}/v/apps/${app.id}`} 
                                                                target="_blank" 
                                                                rel="noopener noreferrer"
                                                                title={`Click to open Spark Form for ${app.name}`}
                                                            >
                                                                <span style={{ fontWeight: 'bold' }}>Time Zone: </span>
                                                                {status.timeZones && status.timeZones.length > 0 ? (
                                                                    status.timeZones.join(', ')
                                                                ) : 'None'}
                                                            </CellLink>
                                                        </CellRow>
                                                    </Td>
                                                    <Td>
                                                        <CellRow>
                                                            <span style={{ fontWeight: 'bold' }}>MODEL: </span>
                                                            {app.allowsTemplating ? 'N/A' : (
                                                                <EditableValue
                                                                    title={`Click to edit GenAI Model for ${app.name}`}
                                                                    onClick={() => startEditGenAIModel(app.id!, app)}
                                                                >
                                                                    {getFeatureFlagSettingValue(app, FoodOrderingGenAIModelSettingId) || 'Click to edit'}
                                                                </EditableValue>
                                                            )}
                                                        </CellRow>
                                                        <CellRow style={{ display: 'flex', alignItems: 'center' }}>
                                                            <span style={{ fontWeight: 'bold', marginRight: '4px' }}>PROMPT: </span>
                                                            {app.allowsTemplating ? 'N/A' : (
                                                                <EditableValue 
                                                                    style={{ 
                                                                        maxWidth: '180px', 
                                                                        overflow: 'hidden', 
                                                                        textOverflow: 'ellipsis', 
                                                                        whiteSpace: 'nowrap'
                                                                    }} 
                                                                    title={getFeatureFlagSettingValue(app, FoodOrderingGenAIPromptOverrideSettingId) ? `Click to edit GenAI Prompt Override (Hover to see full text):\n${getFeatureFlagSettingValue(app, FoodOrderingGenAIPromptOverrideSettingId)}` : `Click to set custom GenAI Prompt Override`}
                                                                    onClick={() => startEditGenAIPrompt(app.id!, app)}
                                                                >
                                                                    {getFeatureFlagSettingValue(app, FoodOrderingGenAIPromptOverrideSettingId) || 'Click to edit'}
                                                                </EditableValue>
                                                            )}
                                                        </CellRow>
                                                    </Td>
                                                    <Td style={{ textAlign: 'right', verticalAlign: 'middle' }}>
                                                        <Button
                                                            themes={['secondary-small']}
                                                            text={app.allowsTemplating ? 'Sync' : (status.status !== 'Idle' ? status.status : (status.hasModules ? 'Sync & Publish' : 'Publish'))}
                                                            onClick={() => handleSyncAndPublishLocation(app)}
                                                            disabled={isBulkProcessing || (status.status !== 'Idle' && status.status !== 'Complete' && status.status !== 'Failed')}
                                                        />
                                                    </Td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </Table>
                            </TableContainer>
                        )}
                    </CollapsibleBody>
                )}
            </CollapsibleCard>

            <PageError errors={errors} onClear={() => setErrors([])} type="error" />
            <PageError errors={successes} onClear={() => setSuccesses([])} type="success" />

            {/* Connection Edit Modal */}
            {editingAppId && editType && (
                <ModalOverlay onClick={closeTelemetryModal}>
                    <ModalContent onClick={(e) => e.stopPropagation()}>
                        <ModalTitle>
                            Edit {editType === 'asr' ? 'ASR Provider' : (editType === 'proactive' ? 'Proactive Prompt' : (editType === 'voice' ? 'Voice' : (editType === 'genaiModel' ? 'Ordering GenAI Model' : 'Ordering GenAI Prompt Override')))}
                        </ModalTitle>
                        {editingDeployment && (
                            <div style={{ fontSize: '13px', color: '#666', marginTop: '-8px' }}>
                                Connection: {editingDeployment.phoneNumber}
                            </div>
                        )}

                        {isLoadingOnDemand ? (
                            <LoaderContainer style={{ padding: '24px 0' }}>
                                <Loader />
                                <LoadingText>Loading active configurations from server...</LoadingText>
                            </LoaderContainer>
                        ) : (
                            <>
                                {editType === 'genaiModel' && (
                                    <ModalFormGroup>
                                        <label>Ordering GenAI Model</label>
                                        <select 
                                            value={editGenAIModel} 
                                            onChange={(e) => setEditGenAIModel(e.target.value)}
                                        >
                                            {GenAIModels.map(model => (
                                                <option key={model.value} value={model.value}>
                                                    {model.label}
                                                </option>
                                            ))}
                                        </select>
                                    </ModalFormGroup>
                                )}

                                {editType === 'genaiPrompt' && (
                                    <ModalFormGroup>
                                        <label>Ordering GenAI Prompt Override</label>
                                        <textarea 
                                            rows={8}
                                            placeholder="Enter prompting to override food ordering GenAI model behavior..."
                                            value={editGenAIPrompt} 
                                            onChange={(e) => setEditGenAIPrompt(e.target.value)}
                                        />
                                    </ModalFormGroup>
                                )}

                                {editType === 'asr' && (
                                    <ModalFormGroup>
                                        <label>ASR Provider</label>
                                        <select 
                                            value={editAsrProvider} 
                                            onChange={(e) => setEditAsrProvider(e.target.value)}
                                        >
                                            <option value="Deepgram">Deepgram</option>
                                            <option value="Soniox">Soniox</option>
                                            <option value="Google">Google</option>
                                        </select>
                                    </ModalFormGroup>
                                )}

                                {editType === 'proactive' && (
                                    <ModalFormGroup>
                                        <label>Proactive Response Prompt</label>
                                        <textarea 
                                            rows={5}
                                            placeholder="Enter prompting to generate proactive responses..."
                                            value={editProactivePrompt} 
                                            onChange={(e) => setEditProactivePrompt(e.target.value)}
                                        />
                                    </ModalFormGroup>
                                )}

                                {editType === 'voice' && (
                                    <ModalFormGroup>
                                        <label>Voice Selection</label>
                                        <select 
                                            value={voiceOptions.some(o => o.voice === editVoiceString) ? editVoiceString : 'custom'} 
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                if (val !== 'custom') {
                                                    setEditVoiceString(val);
                                                } else {
                                                    setEditVoiceString('');
                                                }
                                            }}
                                        >
                                            {voiceOptions.map(opt => (
                                                <option key={opt.voice} value={opt.voice}>{opt.label}</option>
                                            ))}
                                            <option value="custom">Custom Voice String</option>
                                        </select>
                                        
                                        {(!voiceOptions.some(o => o.voice === editVoiceString) || editVoiceString === '') && (
                                            <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                <label style={{ fontSize: '11px', color: '#777' }}>ElevenLabs Custom Voice String</label>
                                                <textarea 
                                                    rows={3}
                                                    placeholder="voice_id=...|model_id=..."
                                                    value={editVoiceString} 
                                                    onChange={(e) => setEditVoiceString(e.target.value)}
                                                />
                                            </div>
                                        )}
                                    </ModalFormGroup>
                                )}
                            </>
                        )}

                        <ModalButtons>
                            <Button 
                                themes={['secondary-small']}
                                text="Cancel"
                                onClick={closeTelemetryModal}
                                disabled={isSavingTelemetry || isLoadingOnDemand}
                            />
                            <Button 
                                themes={['primary-small']}
                                text={isSavingTelemetry ? 'Saving...' : 'Save Settings'}
                                onClick={handleSaveTelemetrySettings}
                                disabled={isSavingTelemetry || isLoadingOnDemand}
                            />
                        </ModalButtons>
                    </ModalContent>
                </ModalOverlay>
            )}
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
`;

const SaveButtonWrapper = styled.div`
    display: flex;
    justify-content: flex-end;
    margin-top: 16px;
    button {
        margin: 0;
    }
`;

const CollapsibleCard = styled.div`
    border-radius: 10px;
    background-color: #ffffff;
    border: solid 1px ${silver_two};
    margin-bottom: 32px;
    display: flex;
    flex-direction: column;
`;

const CollapsibleHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    cursor: pointer;
    padding: 32px;
    user-select: none;

    h3 {
        font-family: Muli;
        font-size: 23px;
        font-weight: 600;
        margin: 0;
        color: ${dark_grey_blue};
    }
`;

const ToggleIcon = styled.span`
    font-size: 16px;
    color: ${color_shades_dark};
`;

const CollapsibleBody = styled.div`
    border-top: solid 1px ${silver_two};
    padding: 8px 16px;
`;

const BulkActionsBar = styled.div`
    display: flex;
    justify-content: flex-end;
    align-items: center;
    margin-bottom: 24px;
    gap: 16px;
    flex-wrap: wrap;
    button {
        margin: 0;
    }
`;

const SearchInput = styled.input`
    padding: 8px 16px;
    font-family: Muli;
    font-size: 14px;
    border: solid 1px ${silver_two};
    border-radius: 6px;
    outline: none;
    width: 250px;
    &:focus {
        border-color: #007dbb;
    }
`;

const SelectAllLiveButton = styled.span`
    font-family: Muli;
    font-size: 14px;
    font-weight: 600;
    color: #007dbb;
    cursor: pointer;
    margin-right: auto;
    user-select: none;
    &:hover {
        text-decoration: underline;
    }
`;

const BulkButtonsGroup = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
`;

const TableContainer = styled.div`
    width: 100%;
    overflow-x: auto;
    margin-top: 12px;
`;

const Table = styled.table`
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
    font-family: Muli;
    min-width: 1200px;
`;

const Th = styled.th`
    text-align: left;
    padding: 14px 16px;
    background-color: #f7f9fc;
    color: ${dark_grey_blue};
    font-weight: 700;
    border-bottom: solid 2px ${silver_two};
    font-size: 14px;

    &:first-of-type {
        position: sticky;
        left: 0;
        z-index: 2;
        background-color: #f7f9fc;
        border-right: solid 2px ${silver_two};
    }
`;

const Td = styled.td`
    padding: 14px 16px;
    border-bottom: solid 1px ${silver_two};
    color: #444;
    font-size: 14px;
    vertical-align: middle;

    &:first-of-type {
        position: sticky;
        left: 0;
        z-index: 1;
        background-color: #ffffff;
        border-right: solid 1px ${silver_two};
    }
`;

const StatusBadge = styled.span<{ status?: string }>`
    display: inline-block;
    padding: 6px 12px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 700;
    background-color: ${props => {
        if (props.status?.includes('Syncing') || props.status?.includes('Publishing')) return 'rgba(0, 123, 255, 0.1)';
        if (props.status === 'Complete') return '#d4edda';
        if (props.status === 'Failed') return '#f8d7da';
        return '#f1f3f5';
    }};
    color: ${props => {
        if (props.status?.includes('Syncing') || props.status?.includes('Publishing')) return '#0056b3';
        if (props.status === 'Complete') return '#155724';
        if (props.status === 'Failed') return '#721c24';
        return '#495057';
    }};
`;

const LoaderContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: 48px;
    gap: 16px;
    background-color: #ffffff;
`;

const LoadingText = styled.p`
    font-family: Muli;
    font-size: 14px;
    color: ${dark_grey_blue};
    margin: 0;
`;

const EmptyState = styled.div`
    font-family: Muli;
    font-size: 14px;
    color: #777;
    text-align: center;
    padding: 32px;
    border: dashed 1px ${silver_two};
    border-radius: 8px;
`;

const LogSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 24px;
`;

const LogHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-family: Muli;
    font-size: 14px;
    font-weight: 700;
    color: ${dark_grey_blue};
    button {
        margin: 0;
    }
`;

const LogArea = styled.div`
    max-height: 140px;
    overflow-y: auto;
    padding: 12px 16px;
    background: #fdfdfd;
    border: 1px solid ${silver_two};
    border-radius: 6px;
    font-family: 'Monaco', 'Menlo', 'Consolas', monospace;
    font-size: 11px;
    color: #666;
    line-height: 1.5;
`;

const LogLine = styled.div`
    margin-bottom: 4px;
    word-break: break-all;
    white-space: pre-wrap;
`;

const CellRow = styled.div`
    font-size: 13px;
    font-weight: 400;
    color: #444;
    margin-bottom: 4px;
    white-space: nowrap;
`;

const CellLink = styled.a`
    font-size: 13px;
    font-weight: 400;
    color: #444;
    text-decoration: none;
    transition: all 0.2s;
    border-bottom: 1px dashed transparent;
    cursor: pointer;

    &:hover {
        color: #007dbb;
        border-bottom-color: #007dbb;
    }
`;

const ConnectionBlock = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    min-height: 48px;
`;

const LocationNameWrapper = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    white-space: nowrap;
`;

const InlineLivePill = styled.span<{ isLive: boolean }>`
    display: inline-block;
    padding: 2px 6px;
    border-radius: 12px;
    font-size: 9px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    background-color: ${props => props.isLive ? '#e6fffa' : '#edf2f7'};
    color: ${props => props.isLive ? '#319795' : '#4a5568'};
    border: 1px solid ${props => props.isLive ? '#b2f5ea' : '#cbd5e0'};
    transition: all 0.2s;
`;

const ModalOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.4);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 2000;
`;

const ModalContent = styled.div`
    background: white;
    padding: 32px;
    border-radius: 12px;
    width: 460px;
    display: flex;
    flex-direction: column;
    gap: 20px;
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.15);
`;

const ModalTitle = styled.h4`
    font-family: Muli;
    font-size: 20px;
    font-weight: 700;
    color: ${dark_grey_blue};
    margin: 0;
`;

const ModalFormGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    
    label {
        font-family: Muli;
        font-size: 13px;
        font-weight: 700;
        color: #555;
    }

    select, textarea, input {
        padding: 10px 14px;
        border-radius: 6px;
        border: solid 1px ${silver_two};
        outline: none;
        font-family: Muli;
        font-size: 14px;
        &:focus {
            border-color: #007dbb;
        }
    }
`;

const ModalButtons = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    margin-top: 8px;
    button {
        margin: 0;
    }
`;

const EditableValue = styled.span`
    cursor: pointer;
    border-bottom: 1px dashed ${silver_two};
    transition: all 0.2s;
    &:hover {
        color: #007dbb;
        border-bottom-color: #007dbb;
    }
`;

const ThContent = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    cursor: pointer;
    user-select: none;
    &:hover {
        color: #007dbb;
    }
`;

const SortIndicator = styled.span`
    font-size: 11px;
    color: #a0aec0;
`;

const ColumnFilterInput = styled.input`
    width: 100%;
    padding: 6px 10px;
    border-radius: 4px;
    border: solid 1px ${silver_two};
    font-family: Muli;
    font-size: 11px;
    outline: none;
    box-sizing: border-box;
    margin-top: 6px;
    font-weight: normal;
    color: #444;
    &:focus {
        border-color: #007dbb;
    }
`;

const ColumnFilterSelect = styled.select`
    width: 100%;
    padding: 6px 10px;
    border-radius: 4px;
    border: solid 1px ${silver_two};
    font-family: Muli;
    font-size: 11px;
    outline: none;
    box-sizing: border-box;
    margin-top: 6px;
    font-weight: normal;
    color: #444;
    background-color: #ffffff;
    &:focus {
        border-color: #007dbb;
    }
`;

const LocationLink = styled.a`
    font-size: 13px;
    font-weight: 700;
    color: ${dark_grey_blue};
    text-decoration: none;
    transition: all 0.2s;
    border-bottom: 1px dashed transparent;

    &:hover {
        color: #007dbb;
        border-bottom-color: #007dbb;
    }
`;

const NoModuleBadge = styled.span`
    font-family: Muli;
    font-size: 13px;
    color: #718096;
    font-style: italic;
`;

export default SparkOrganizationManagementSettings;
